
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Video, Shield, Clock, Globe, CheckCircle } from 'lucide-react';

const OnlineTherapy = () => {
  const { t, getLocalizedPath } = useLanguage();

  return (
    <>
      <Helmet>
        <title>Online Therapy - Secure Video Sessions with Therapists, Psychologists & Psychotherapists</title>
        <meta name="description" content="Experience secure online therapy sessions with certified therapists, licensed psychologists & professional psychotherapists worldwide. Connect from anywhere, anytime." />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Hero Section */}
        <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-100">
          <div className="container mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Online Therapy with Therapists, Psychologists & Psychotherapists
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Connect with certified therapists, licensed psychologists, and professional psychotherapists from anywhere in the world through secure, 
              high-quality video sessions designed for your privacy and convenience.
            </p>
            <Button size="lg" asChild>
              <Link to={getLocalizedPath('/find')}>
                Find Online Therapist or Psychologist
              </Link>
            </Button>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Why Choose Online Therapy?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <Video className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">HD Video Quality</h3>
                <p className="text-gray-600">Crystal clear video and audio for effective communication</p>
              </div>
              <div className="text-center">
                <Shield className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">100% Secure</h3>
                <p className="text-gray-600">End-to-end encryption ensures complete privacy</p>
              </div>
              <div className="text-center">
                <Clock className="h-12 w-12 text-purple-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Flexible Scheduling</h3>
                <p className="text-gray-600">Book sessions that fit your busy lifestyle</p>
              </div>
              <div className="text-center">
                <Globe className="h-12 w-12 text-red-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Global Access</h3>
                <p className="text-gray-600">Connect with therapists worldwide</p>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 px-4 bg-gray-50">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">How Online Therapy Works</h2>
            <div className="max-w-4xl mx-auto">
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">1</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Choose Your Therapist</h3>
                    <p className="text-gray-600">Browse profiles and select a therapist that matches your needs and preferences.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">2</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Schedule Your Session</h3>
                    <p className="text-gray-600">Book a convenient time slot that works for both you and your therapist.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mr-4 mt-1">3</div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Join Your Video Session</h3>
                    <p className="text-gray-600">Connect through our secure platform from any device, anywhere in the world.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">Platform Features</h2>
            <div className="max-w-3xl mx-auto">
              <div className="space-y-4">
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span>HIPAA-compliant security standards</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span>Works on desktop, tablet, and mobile devices</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span>No software downloads required</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span>24/7 technical support</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span>Session recording available (with consent)</span>
                </div>
                <div className="flex items-center">
                  <CheckCircle className="h-6 w-6 text-green-600 mr-3" />
                  <span>Integrated scheduling and payment system</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-blue-600 text-white text-center">
          <div className="container mx-auto">
            <h2 className="text-3xl font-bold mb-6">Start Your Online Therapy Journey</h2>
            <p className="text-xl mb-8">Connect with a certified therapist today</p>
            <Button size="lg" variant="secondary" asChild>
              <Link to={getLocalizedPath('/find')}>
                Find Your Therapist
              </Link>
            </Button>
          </div>
        </section>
      </div>
    </>
  );
};

export default OnlineTherapy;
